import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, Lightbulb, RefreshCw } from 'lucide-react';
import {
  getCatalogSuggestions,
  reviewCatalogSuggestion,
  getInventorySettings,
  getInventoryItems,
  listRestaurants,
} from '../services/api';
import { CANONICAL_UNITS } from '../utils/units';
import { KINDS } from '../utils/inventoryForm';
import { inventoryErrorMessage } from '../utils/inventoryErrors';
import {
  SUGGESTION_STATUSES,
  NAME_MAX,
  CATEGORY_MAX,
  REASON_MAX,
  prepareApprovalForm,
  validateApprovalForm,
  buildApprovalOverrides,
  sortSuggestions,
  countByStatus,
  responseErrorCode,
} from '../utils/catalogSuggestions';
import HelpIcon from '../components/help/HelpIcon';
import SuggestInventoryItemModal from '../components/SuggestInventoryItemModal';
import useEscapeKey from '../hooks/useEscapeKey';
import './CatalogSuggestionsPage.css';

const TABS = [...SUGGESTION_STATUSES, 'all'];
const FIELD_MAX = { name: NAME_MAX, category: CATEGORY_MAX, rejectionReason: REASON_MAX };
const INLINE_CODES = ['REQUIRED', 'TOO_LONG', 'INVALID_UNIT', 'INVALID_NUMBER', 'INVALID_KIND'];

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

/**
 * RES-46 — Sugerencias de insumos.
 *
 * - Quien administra la organización (`can.suggestionsReview`): bandeja por estado con
 *   pendientes primero; aprueba (ajustando nombre/unidad/categoría/tipo, costo y mínimo) o
 *   rechaza con motivo. Aprobar crea el insumo en el catálogo de todos los locales.
 * - Una sucursal: "Mis sugerencias" en solo lectura y el botón "Sugerir insumo".
 */
function CatalogSuggestionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [can, setCan] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [tab, setTab] = useState(null);
  const [restaurantFilter, setRestaurantFilter] = useState('');
  const [suggestOpen, setSuggestOpen] = useState(false);
  // Revisión en curso: { mode: 'approve' | 'reject', suggestion }
  const [review, setReview] = useState(null);
  const [approvalForm, setApprovalForm] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [reviewErrors, setReviewErrors] = useState({});
  const [reviewError, setReviewError] = useState(null);
  const [reviewing, setReviewing] = useState(false);

  const isReviewer = can?.suggestionsReview === true;
  const canSuggest = can?.suggestionsWrite === true && !isReviewer;

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 5000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const loadSuggestions = useCallback(async () => {
    try {
      const response = await getCatalogSuggestions();
      setSuggestions(response?.suggestions || []);
      setLoadError(false);
    } catch (error) {
      console.error('Error loading catalog suggestions:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getInventorySettings()
      .then((settings) => setCan(settings?.can || {}))
      .catch(() => setCan({}));
    listRestaurants()
      .then((data) => setRestaurants(Array.isArray(data) ? data : data?.restaurants || []))
      .catch(() => setRestaurants([]));
    // Categorías existentes para el datalist (sugerir y aprobar)
    getInventoryItems()
      .then((data) =>
        setCategories(
          [...new Set((data?.items || []).map((item) => item.category).filter(Boolean))].sort()
        )
      )
      .catch(() => setCategories([]));
    loadSuggestions();
  }, [loadSuggestions]);

  // La organización arranca en "pendientes"; la sucursal ve todo lo que pidió
  const activeTab = tab || (isReviewer ? 'pending' : 'all');

  const restaurantName = useCallback(
    (restaurantId) => {
      const restaurant = restaurants.find((r) => r.restaurantId === restaurantId);
      return restaurant?.name || restaurantId || '-';
    },
    [restaurants]
  );

  const scoped = useMemo(
    () =>
      restaurantFilter
        ? suggestions.filter((s) => s.restaurantId === restaurantFilter)
        : suggestions,
    [suggestions, restaurantFilter]
  );
  const counts = useMemo(() => countByStatus(scoped), [scoped]);
  const visible = useMemo(
    () =>
      sortSuggestions(activeTab === 'all' ? scoped : scoped.filter((s) => s.status === activeTab)),
    [scoped, activeTab]
  );
  const restaurantOptions = useMemo(() => {
    const ids = [...new Set(suggestions.map((s) => s.restaurantId).filter(Boolean))];
    return ids
      .map((id) => ({ id, name: String(restaurantName(id)) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [suggestions, restaurantName]);

  const closeReview = useCallback(() => {
    if (reviewing) return;
    setReview(null);
    setApprovalForm(null);
    setRejectionReason('');
    setReviewErrors({});
    setReviewError(null);
  }, [reviewing]);

  useEscapeKey(Boolean(review), closeReview);

  const openApprove = (suggestion) => {
    setReview({ mode: 'approve', suggestion });
    setApprovalForm(prepareApprovalForm(suggestion));
    setReviewErrors({});
    setReviewError(null);
  };

  const openReject = (suggestion) => {
    setReview({ mode: 'reject', suggestion });
    setRejectionReason('');
    setReviewErrors({});
    setReviewError(null);
  };

  const setApprovalField = (field, value) => {
    setApprovalForm((prev) => ({ ...prev, [field]: value }));
    if (reviewErrors[field]) setReviewErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const fieldError = (field) => {
    const code = reviewErrors[field];
    if (!code) return null;
    if (code === 'TOO_LONG') {
      return t('inventory.suggestions.errors.tooLong', { max: FIELD_MAX[field] });
    }
    if (code === 'ALREADY_IN_CATALOG') {
      return t('inventory.suggestions.errors.alreadyInCatalogApprove');
    }
    return t(`inventory.errors.${code}`);
  };

  const replaceSuggestion = (updated) => {
    if (!updated?.suggestionId) return;
    setSuggestions((prev) =>
      prev.map((s) => (s.suggestionId === updated.suggestionId ? { ...s, ...updated } : s))
    );
  };

  const handleReviewFailure = (error) => {
    console.error('Error reviewing catalog suggestion:', error);
    const code = responseErrorCode(error);
    const field = error?.response?.data?.field;
    setReviewing(false);
    if (code === 'ALREADY_REVIEWED') {
      // Otra persona la revisó mientras tanto: se cierra y se recarga la bandeja
      setReview(null);
      setFeedback({ type: 'error', message: t('inventory.suggestions.errors.alreadyReviewed') });
      loadSuggestions();
      return;
    }
    if (code === 'ALREADY_IN_CATALOG') {
      setReviewErrors((prev) => ({ ...prev, name: code }));
    } else if (
      INLINE_CODES.includes(code) &&
      field &&
      (field === 'rejectionReason' || (approvalForm && field in approvalForm))
    ) {
      setReviewErrors((prev) => ({ ...prev, [field]: code }));
    } else {
      setReviewError(inventoryErrorMessage(t, error, 'inventory.suggestions.errors.reviewError'));
    }
  };

  const handleApprove = async (event) => {
    event.preventDefault();
    const found = validateApprovalForm(approvalForm);
    if (Object.keys(found).length > 0) {
      setReviewErrors(found);
      return;
    }
    const { suggestion } = review;
    const item = buildApprovalOverrides(suggestion, approvalForm);
    const body = { decision: 'approve' };
    if (Object.keys(item).length > 0) body.item = item;
    setReviewing(true);
    setReviewError(null);
    try {
      const result = await reviewCatalogSuggestion(suggestion.suggestionId, body);
      replaceSuggestion(
        result?.suggestion || { suggestionId: suggestion.suggestionId, status: 'approved' }
      );
      setReviewing(false);
      setReview(null);
      setFeedback({
        type: 'success',
        message: t('inventory.suggestions.approvedFeedback', {
          name: result?.item?.name || item.name || suggestion.name,
        }),
      });
    } catch (error) {
      handleReviewFailure(error);
    }
  };

  const handleReject = async (event) => {
    event.preventDefault();
    const reason = rejectionReason.trim();
    if (!reason) {
      setReviewErrors({ rejectionReason: 'REQUIRED' });
      return;
    }
    if (reason.length > REASON_MAX) {
      setReviewErrors({ rejectionReason: 'TOO_LONG' });
      return;
    }
    const { suggestion } = review;
    setReviewing(true);
    setReviewError(null);
    try {
      const result = await reviewCatalogSuggestion(suggestion.suggestionId, {
        decision: 'reject',
        rejectionReason: reason,
      });
      replaceSuggestion(
        result?.suggestion || {
          suggestionId: suggestion.suggestionId,
          status: 'rejected',
          rejectionReason: reason,
        }
      );
      setReviewing(false);
      setReview(null);
      setFeedback({ type: 'success', message: t('inventory.suggestions.rejectedFeedback') });
    } catch (error) {
      handleReviewFailure(error);
    }
  };

  const handleCreated = (suggestion) => {
    setSuggestOpen(false);
    if (suggestion?.suggestionId) setSuggestions((prev) => [suggestion, ...prev]);
    setFeedback({ type: 'success', message: t('inventory.suggestions.createdFeedback') });
  };

  const kindLabel = (kind) => (KINDS.includes(kind) ? t(`inventory.kinds.${kind}`) : null);

  // Se espera también a los permisos para no mostrar un instante la vista equivocada
  if (loading || can === null) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="catalog-suggestions-page">
      <div className="page-header">
        <div className="catalog-suggestions-title">
          <button
            type="button"
            className="btn btn-ghost btn-sm catalog-suggestions-back"
            onClick={() => navigate('/inventory')}
          >
            <ArrowLeft size={16} aria-hidden="true" />
            {t('inventory.suggestions.backToInventory')}
          </button>
          <h1>
            {isReviewer
              ? t('inventory.suggestions.title')
              : t('inventory.suggestions.mySuggestions')}{' '}
            <HelpIcon topicKey="inventory.suggestions" />
          </h1>
          <p>
            {isReviewer
              ? t('inventory.suggestions.subtitleReviewer')
              : t('inventory.suggestions.subtitleBranch')}
          </p>
        </div>
        {canSuggest && (
          <button
            type="button"
            className="btn btn-primary catalog-suggestions-suggest-btn"
            onClick={() => setSuggestOpen(true)}
          >
            <Lightbulb size={16} aria-hidden="true" />
            {t('inventory.suggestions.suggest')}
          </button>
        )}
      </div>

      {feedback && (
        <div
          className={`alert alert-${feedback.type === 'error' ? 'error' : 'success'} catalog-suggestions-feedback`}
          role="alert"
          aria-live="polite"
        >
          {feedback.message}
        </div>
      )}

      {loadError ? (
        <div className="alert alert-error catalog-suggestions-load-error" role="alert">
          <span>{t('inventory.suggestions.errors.loadError')}</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={loadSuggestions}>
            <RefreshCw size={14} aria-hidden="true" />
            {t('common.retry')}
          </button>
        </div>
      ) : (
        <>
          <div className="catalog-suggestions-toolbar">
            <div
              className="catalog-suggestions-tabs"
              role="tablist"
              aria-label={t('inventory.suggestions.filterByStatus')}
            >
              {TABS.map((key) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === key}
                  data-status={key}
                  className={`catalog-suggestions-tab${activeTab === key ? ' is-active' : ''}`}
                  onClick={() => setTab(key)}
                >
                  {t(`inventory.suggestions.tabs.${key}`)}
                  <span className="catalog-suggestions-tab-count">{counts[key] ?? 0}</span>
                </button>
              ))}
            </div>
            {isReviewer && restaurantOptions.length > 1 && (
              <select
                className="catalog-suggestions-restaurant-filter"
                value={restaurantFilter}
                onChange={(e) => setRestaurantFilter(e.target.value)}
                aria-label={t('inventory.suggestions.filterByRestaurant')}
              >
                <option value="">{t('inventory.suggestions.allRestaurants')}</option>
                {restaurantOptions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {visible.length === 0 ? (
            <div className="catalog-suggestions-empty">
              <Lightbulb size={32} aria-hidden="true" />
              <p>
                {activeTab === 'pending'
                  ? t('inventory.suggestions.emptyPending')
                  : t('inventory.suggestions.empty')}
              </p>
            </div>
          ) : (
            <ul className="catalog-suggestions-list">
              {visible.map((s) => (
                <li
                  key={s.suggestionId}
                  className={`catalog-suggestion-card status-${s.status}`}
                  data-suggestion-id={s.suggestionId}
                >
                  <div className="catalog-suggestion-main">
                    <div className="catalog-suggestion-heading">
                      <strong className="catalog-suggestion-name">{s.name}</strong>
                      <span className={`catalog-suggestion-status status-${s.status}`}>
                        {t(`inventory.suggestions.status.${s.status}`)}
                      </span>
                    </div>
                    <div className="catalog-suggestion-meta">
                      <span>{t(`inventory.units.${s.unit}`)}</span>
                      {kindLabel(s.kind) && <span>{kindLabel(s.kind)}</span>}
                      {s.category && <span>{s.category}</span>}
                    </div>
                    <div className="catalog-suggestion-origin">
                      <span className="catalog-suggestion-restaurant">
                        {t('inventory.suggestions.requestedFrom', {
                          restaurant: restaurantName(s.restaurantId),
                        })}
                      </span>
                      {s.createdAt && (
                        <span className="catalog-suggestion-date">{formatDate(s.createdAt)}</span>
                      )}
                    </div>
                    {s.reason && (
                      <p className="catalog-suggestion-reason">
                        <span className="catalog-suggestion-label">
                          {t('inventory.suggestions.reasonLabel')}
                        </span>{' '}
                        {s.reason}
                      </p>
                    )}
                    {s.status === 'rejected' && s.rejectionReason && (
                      <p className="catalog-suggestion-rejection">
                        <span className="catalog-suggestion-label">
                          {t('inventory.suggestions.rejectionReasonLabel')}
                        </span>{' '}
                        {s.rejectionReason}
                      </p>
                    )}
                    {s.status === 'approved' && (
                      <p className="catalog-suggestion-approved-note">
                        {t('inventory.suggestions.approvedNote')}
                      </p>
                    )}
                  </div>
                  {isReviewer && s.status === 'pending' && (
                    <div className="catalog-suggestion-actions">
                      <button
                        type="button"
                        className="btn btn-success btn-sm catalog-suggestion-approve"
                        onClick={() => openApprove(s)}
                      >
                        <Check size={14} aria-hidden="true" />
                        {t('inventory.suggestions.approve')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm catalog-suggestion-reject"
                        onClick={() => openReject(s)}
                      >
                        <X size={14} aria-hidden="true" />
                        {t('inventory.suggestions.reject')}
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {suggestOpen && (
        <SuggestInventoryItemModal
          categories={categories}
          onClose={() => setSuggestOpen(false)}
          onCreated={handleCreated}
        />
      )}

      {review && review.mode === 'approve' && approvalForm && (
        <div className="modal-overlay">
          <div
            className="modal-content catalog-suggestions-review-modal catalog-suggestions-approve-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="approve-suggestion-title"
          >
            <div className="modal-header">
              <h2 id="approve-suggestion-title">{t('inventory.suggestions.approveTitle')}</h2>
              <button
                type="button"
                className="close-btn"
                onClick={closeReview}
                aria-label={t('common.close')}
                disabled={reviewing}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleApprove} noValidate>
              <div className="modal-body">
                <p className="catalog-suggestions-review-intro">
                  {t('inventory.suggestions.approveIntro', {
                    restaurant: restaurantName(review.suggestion.restaurantId),
                  })}
                </p>
                {reviewError && (
                  <div className="alert alert-error catalog-suggestions-review-error" role="alert">
                    {reviewError}
                  </div>
                )}
                <div className={`form-group${reviewErrors.name ? ' has-error' : ''}`}>
                  <label htmlFor="approve-name">{t('inventory.suggestions.form.name')} *</label>
                  <input
                    id="approve-name"
                    type="text"
                    value={approvalForm.name}
                    maxLength={NAME_MAX}
                    onChange={(e) => setApprovalField('name', e.target.value)}
                    aria-required="true"
                    aria-invalid={!!reviewErrors.name}
                  />
                  {reviewErrors.name && (
                    <span className="field-error" role="alert">
                      {fieldError('name')}
                    </span>
                  )}
                </div>
                <div className="form-row">
                  <div className={`form-group${reviewErrors.unit ? ' has-error' : ''}`}>
                    <label htmlFor="approve-unit">{t('inventory.suggestions.form.unit')} *</label>
                    <select
                      id="approve-unit"
                      value={approvalForm.unit}
                      onChange={(e) => setApprovalField('unit', e.target.value)}
                    >
                      {CANONICAL_UNITS.map((unit) => (
                        <option key={unit} value={unit}>
                          {t(`inventory.units.${unit}`)}
                        </option>
                      ))}
                    </select>
                    {reviewErrors.unit && (
                      <span className="field-error" role="alert">
                        {fieldError('unit')}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="approve-kind">{t('inventory.suggestions.form.kind')}</label>
                    <select
                      id="approve-kind"
                      value={approvalForm.kind}
                      onChange={(e) => setApprovalField('kind', e.target.value)}
                    >
                      {KINDS.map((kind) => (
                        <option key={kind} value={kind}>
                          {t(`inventory.kinds.${kind}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className={`form-group${reviewErrors.category ? ' has-error' : ''}`}>
                  <label htmlFor="approve-category">
                    {t('inventory.suggestions.form.category')}
                  </label>
                  <input
                    id="approve-category"
                    type="text"
                    list="approve-categories"
                    value={approvalForm.category}
                    maxLength={CATEGORY_MAX}
                    onChange={(e) => setApprovalField('category', e.target.value)}
                  />
                  {categories.length > 0 && (
                    <datalist id="approve-categories">
                      {categories.map((category) => (
                        <option key={category} value={category} />
                      ))}
                    </datalist>
                  )}
                  {reviewErrors.category && (
                    <span className="field-error" role="alert">
                      {fieldError('category')}
                    </span>
                  )}
                </div>
                <div className="form-row">
                  <div className={`form-group${reviewErrors.refCost ? ' has-error' : ''}`}>
                    <label htmlFor="approve-ref-cost">
                      {t('inventory.suggestions.form.refCost')}
                    </label>
                    <input
                      id="approve-ref-cost"
                      type="number"
                      min="0"
                      step="any"
                      inputMode="decimal"
                      value={approvalForm.refCost}
                      onChange={(e) => setApprovalField('refCost', e.target.value)}
                    />
                    {reviewErrors.refCost && (
                      <span className="field-error" role="alert">
                        {fieldError('refCost')}
                      </span>
                    )}
                  </div>
                  <div className={`form-group${reviewErrors.defaultMinStock ? ' has-error' : ''}`}>
                    <label htmlFor="approve-min-stock">
                      {t('inventory.suggestions.form.defaultMinStock')}
                    </label>
                    <input
                      id="approve-min-stock"
                      type="number"
                      min="0"
                      step="any"
                      inputMode="decimal"
                      value={approvalForm.defaultMinStock}
                      onChange={(e) => setApprovalField('defaultMinStock', e.target.value)}
                    />
                    {reviewErrors.defaultMinStock && (
                      <span className="field-error" role="alert">
                        {fieldError('defaultMinStock')}
                      </span>
                    )}
                  </div>
                </div>
                <p className="catalog-suggestions-review-hint">
                  {t('inventory.suggestions.approveHint')}
                </p>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={closeReview}
                  disabled={reviewing}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary catalog-suggestions-confirm-approve"
                  disabled={reviewing}
                >
                  {t('inventory.suggestions.confirmApprove')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {review && review.mode === 'reject' && (
        <div className="modal-overlay">
          <div
            className="modal-content catalog-suggestions-review-modal catalog-suggestions-reject-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reject-suggestion-title"
          >
            <div className="modal-header">
              <h2 id="reject-suggestion-title">{t('inventory.suggestions.rejectTitle')}</h2>
              <button
                type="button"
                className="close-btn"
                onClick={closeReview}
                aria-label={t('common.close')}
                disabled={reviewing}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleReject} noValidate>
              <div className="modal-body">
                <p className="catalog-suggestions-review-intro">
                  {t('inventory.suggestions.rejectIntro', {
                    name: review.suggestion.name,
                    restaurant: restaurantName(review.suggestion.restaurantId),
                  })}
                </p>
                {reviewError && (
                  <div className="alert alert-error catalog-suggestions-review-error" role="alert">
                    {reviewError}
                  </div>
                )}
                <div className={`form-group${reviewErrors.rejectionReason ? ' has-error' : ''}`}>
                  <label htmlFor="reject-reason">
                    {t('inventory.suggestions.rejectionReasonLabel')} *
                  </label>
                  <textarea
                    id="reject-reason"
                    rows={3}
                    value={rejectionReason}
                    placeholder={t('inventory.suggestions.rejectionReasonPlaceholder')}
                    onChange={(e) => {
                      setRejectionReason(e.target.value);
                      if (reviewErrors.rejectionReason) setReviewErrors({});
                    }}
                    aria-required="true"
                    aria-invalid={!!reviewErrors.rejectionReason}
                  />
                  <span
                    className={`catalog-suggestions-counter${
                      rejectionReason.length > REASON_MAX ? ' is-over' : ''
                    }`}
                  >
                    {rejectionReason.length}/{REASON_MAX}
                  </span>
                  {reviewErrors.rejectionReason && (
                    <span className="field-error" role="alert">
                      {reviewErrors.rejectionReason === 'REQUIRED'
                        ? t('inventory.suggestions.errors.rejectionReasonRequired')
                        : fieldError('rejectionReason')}
                    </span>
                  )}
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={closeReview}
                  disabled={reviewing}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-danger catalog-suggestions-confirm-reject"
                  disabled={reviewing}
                >
                  {t('inventory.suggestions.confirmReject')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CatalogSuggestionsPage;
